/**
 * Client-side mirror of the /api print-evidence photo rules (CORE-13):
 * `apps/api/src/lib/provider-evidence.ts` accepts JPEG, PNG, WebP, GIF, HEIC
 * and HEIF up to 15 MB, and rejects a declared content type outside that set
 * even when the bytes are fine. Checking before staging means a photo the API
 * would refuse never reaches the Mark shipped confirmation, where it used to
 * block shipping with generic copy. The API stays the authority: it still
 * sniffs the bytes and re-checks the size.
 */

/** PROVIDER_EVIDENCE_MAX_IMAGE_BYTES in the /api evidence lane. */
export const EVIDENCE_PHOTO_MAX_BYTES = 15 * 1024 * 1024;

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"] as const;

// Browser / OS spellings that mean an accepted type. "image/jpg" and the
// HEIF sequence types are common declarations the API's strict set rejects.
const TYPE_ALIASES: Record<string, (typeof ACCEPTED_TYPES)[number]> = {
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "image/heic-sequence": "image/heic",
  "image/heif-sequence": "image/heif"
};

// Files picked from some phones and file managers arrive with an empty MIME
// type; the extension is then the only hint (the API still sniffs the bytes).
const EXTENSION_TYPES: Record<string, (typeof ACCEPTED_TYPES)[number]> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jpe: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif"
};

/** `accept` for the photo picker: steers the picker to the supported types. */
export const EVIDENCE_PHOTO_ACCEPT = [
  ...ACCEPTED_TYPES,
  ...Object.keys(EXTENSION_TYPES).map((extension) => `.${extension}`)
].join(",");

type PhotoLike = { name: string; type: string; size: number };

/** The accepted content type to declare for this file, or null when it is
 * not a supported photo. */
export function evidencePhotoContentType(file: Pick<PhotoLike, "name" | "type">): string | null {
  const declared = file.type.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (declared) {
    if ((ACCEPTED_TYPES as readonly string[]).includes(declared)) return declared;
    if (TYPE_ALIASES[declared]) return TYPE_ALIASES[declared];
    // A declared non-image (or an unsupported image such as BMP/TIFF/SVG)
    // is rejected by the API even when the extension looks right.
    if (declared !== "application/octet-stream") return null;
  }
  const extension = /\.([a-z0-9]+)$/iu.exec(file.name.trim())?.[1]?.toLowerCase();
  return extension ? EXTENSION_TYPES[extension] ?? null : null;
}

export type EvidencePhotoProblem = "type" | "size" | "empty";

/** Why the API would refuse this photo, or null when it can be staged. */
export function checkEvidencePhoto(file: PhotoLike): EvidencePhotoProblem | null {
  if (evidencePhotoContentType(file) === null) return "type";
  if (file.size <= 0) return "empty";
  if (file.size > EVIDENCE_PHOTO_MAX_BYTES) return "size";
  return null;
}
